/**
 * ErrorBoundary - React Error Boundary Component
 *
 * Catches JavaScript errors in child component tree and displays a fallback UI.
 * Each portal (Beneficiary, Admin, Hub) should have its own error boundary to prevent
 * errors in one portal from crashing another.
 *
 * This is application infrastructure (like guards), not a UI component.
 *
 * Features:
 * - Catches render errors, lifecycle errors, and errors in constructors
 * - Displays portal-specific fallback UI
 * - Provides retry functionality
 * - Reports errors to monitoring (Sentry)
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { getLogger, getMonitoring } from "@/infrastructure/di";
import { forceNavigate } from "@/infrastructure/navigation";
import { cn } from "@/utils";

type ErrorBoundaryVariant = "default" | "admin" | "hub";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Portal variant for styling the error UI */
  variant?: ErrorBoundaryVariant;
  /** Custom fallback component */
  fallback?: ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Get variant-specific styles based on portal theme
 */
const getVariantStyles = (variant: ErrorBoundaryVariant) => {
  switch (variant) {
    case "hub":
      return {
        container:
          "bg-gradient-to-b from-[var(--sidebar-hub-bg-from)] to-[var(--sidebar-hub-bg-to)]",
        card: "bg-info-dark/30 border-info-active/30",
        title: "text-text-inverse",
        message: "text-border-info-light",
        code: "text-info/50",
        buttonPrimary: "bg-bg-primary text-info-dark hover:bg-info-surface",
        buttonSecondary: "border-info/50 text-border-info-light hover:bg-info-dark/50",
        footer: "bg-info-dark/50 border-info-active/30 text-info",
        link: "text-border-info-light hover:text-text-inverse",
      };
    default:
      return {
        container: "bg-gradient-to-b from-bg-secondary to-bg-tertiary",
        card: "bg-bg-primary border-border-secondary",
        title: "text-text-primary",
        message: "text-text-secondary",
        code: "text-text-muted-inverse",
        buttonPrimary: "bg-primary-hover text-text-inverse hover:bg-primary",
        buttonSecondary: "border-border-primary text-text-secondary hover:bg-bg-secondary",
        footer: "bg-bg-secondary border-border-tertiary text-text-tertiary",
        link: "text-text-secondary hover:text-text-primary",
      };
  }
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const variant = this.props.variant || "default";
    /**
     * Report to monitoring. The `error_boundary` + `route` tags let ops
     * filter "all crashes in the hub portal" or "all crashes on /investments"
     * without crawling the breadcrumb timeline.
     */
    getMonitoring().captureException(error, {
      tags: {
        error_boundary: variant,
        route: window.location.pathname,
      },
      extra: {
        componentStack: errorInfo.componentStack,
        variant,
      },
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Log in development
    if (import.meta.env.DEV) {
      getLogger().error("[ErrorBoundary] Caught error:", error);
      getLogger().error("[ErrorBoundary] Component stack:", errorInfo.componentStack);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = (): void => {
    forceNavigate("/");
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, variant = "default", fallback } = this.props;

    if (!hasError) {
      return children;
    }

    // Use custom fallback if provided
    if (fallback) {
      return fallback;
    }

    const styles = getVariantStyles(variant);

    return (
      <div className={cn("min-h-screen flex items-center justify-center px-4", styles.container)}>
        <div className="w-full max-w-[var(--modal-width-lg)]">
          <div className={cn("rounded-lg shadow-sm overflow-hidden border", styles.card)}>
            <div className="p-6">
              <div className="flex items-center gap-6">
                {/* Error Code */}
                <div className="flex-shrink-0">
                  <div className={cn("text-7xl font-black select-none", styles.code)}>!</div>
                </div>

                {/* Error Details */}
                <div className="flex-1">
                  <h1 className={cn("text-xl font-bold mb-1", styles.title)}>
                    Something went wrong
                  </h1>
                  <p className={cn("text-xs mb-3", styles.message)}>
                    An unexpected error occurred. Please try again or contact support if the problem
                    persists.
                  </p>

                  {/* Show error message in development */}
                  {import.meta.env.DEV && error && (
                    <pre
                      className={cn(
                        "text-xs mb-3 p-2 rounded overflow-auto max-h-24",
                        styles.message,
                        "bg-black/10",
                      )}
                    >
                      {error.message}
                    </pre>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={this.handleRetry}
                      className={cn(
                        "inline-flex items-center justify-center px-3 py-1.5 border rounded text-xs font-medium transition-colors",
                        styles.buttonSecondary,
                      )}
                    >
                      Try Again
                    </button>
                    <button
                      onClick={this.handleGoHome}
                      className={cn(
                        "inline-flex items-center justify-center px-3 py-1.5 rounded text-xs font-medium transition-colors",
                        styles.buttonPrimary,
                      )}
                    >
                      Go to Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={cn("px-6 py-3 border-t", styles.footer)}>
              <p className="text-xs">
                Need help?{" "}
                <a href="mailto:support@hulahoop.media" className={cn("underline", styles.link)}>
                  support@hulahoop.media
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
